import { Box, ClickAwayListener, Grid, Popper, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BasicTable,
  Breadcrumbs,
  Button,
  CardComponent,
  Filter,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React, { ChangeEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MA_DUE_DILIGENCE } from '../../core-utils/routes';
import Loader from '../../_components/molecules/Loader';
import CategoryTable from '../../_components/atoms/CategoryTable';
import { RECOMMENDED_APPS } from '../../core-utils/messages/RecommendedAppConstants';
import Typography from '@HomaHealth/ui-component-library/lib/_patient-access-components/atoms/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutocompleteWithCheckBoxes from '../../_components/atoms/Autocomplete';
import {
  getConfidenceScoreOptions,
  getSelectedConfidenceScoreKeys,
  formatCategoryTableData,
  MADueDiligenceInternalTableColumns,
  documentsTableColumns,
  formatDocumentTableData,
} from '../../core-utils/helper-utils/recommendedAppsHelper';
import TabSelection from '../../_components/molecules/TabSelection';
import {
  DueDiligenceProvider,
  useDueDiligenceContext,
} from './DueDiligenceContext';
import { CircularLoader } from '@HomaHealth/ui-component-library';
import BasicModal from '../../_components/atoms/BasicModal';
import MADueDiligenceModal, {
  MADueDiligenceModalProps,
} from '../../_components/organisms/MADueDiligenceModal';

const messages = RECOMMENDED_APPS.MA_DUE_DILIGENCE;

const FilterComponent: React.FC<{}> = () => {
  const { classes } = makeStyles()({
    filterContainer: {
      margin: '1rem 0 3rem 0',
      display: 'flex',
      gap: '1rem',
    },
  })();

  const filters = RECOMMENDED_APPS.MA_DUE_DILIGENCE.FILTERS;
  const confidenceScores = getConfidenceScoreOptions();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const {
    parameters,
    selectedFilters,
    setSelectedFilters,
    selectedParameters,
    setselectedParameters,
    selectedConfidenceScore,
    setselectedConfidenceScore,
    getDueDiligenceForGroup,
  } = useDueDiligenceContext();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const handleSelectedFiltersChange = (event: any, value: any) => {
    setSelectedFilters(value);
  };
  const handleSelectedParametersChanges = (event: any, value: any) => {
    setselectedParameters(value);
  };
  const handleSelectedConfidenceScoresChanges = (event: any, value: any) => {
    setselectedConfidenceScore(value);
  };
  const handleFilterDataDisabled = () => {
    if (filters.every((filter) => selectedFilters.includes(filter))) {
      return (
        selectedParameters.length === 0 && selectedConfidenceScore.length === 0
      );
    } else if (selectedFilters.includes('Parameters')) {
      return selectedParameters.length === 0;
    } else {
      return selectedConfidenceScore.length === 0;
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  return (
    <Grid className={classes.filterContainer}>
      <Button
        aria-describedby={id}
        label="Add Filter"
        startIcon={<Filter />}
        onClick={handleClick}
        height="34px"
      />
      {selectedFilters.includes('Parameters') && (
        <AutocompleteWithCheckBoxes
          placeholder="Parameters"
          options={parameters}
          handleChange={handleSelectedParametersChanges}
          width="200px"
          dropdownWidth="400px"
          fullWidthText
          disableClearAll={false}
          defaultValue={selectedParameters}
        />
      )}
      {selectedFilters.includes('Confidence Score') && (
        <AutocompleteWithCheckBoxes
          placeholder="Confidence Score"
          options={confidenceScores}
          handleChange={handleSelectedConfidenceScoresChanges}
          width="200px"
          dropdownWidth="400px"
          fullWidthText
          disableClearAll={false}
          defaultValue={selectedConfidenceScore}
        />
      )}
      {selectedFilters.length > 0 && (
        <Button
          label="Get Filtered Data"
          width="auto"
          variant="contained"
          onClick={() => {
            getDueDiligenceForGroup(
              selectedParameters,
              getSelectedConfidenceScoreKeys(selectedConfidenceScore),
            );
          }}
          height="34px"
          disabled={handleFilterDataDisabled()}
        />
      )}
      {selectedFilters.length > 0 && (
        <Button
          label="Reset Filter"
          width="auto"
          variant="outlined"
          onClick={() => {
            getDueDiligenceForGroup([], []);
            setSelectedFilters([]);
            setselectedParameters([]);
            setselectedConfidenceScore([]);
          }}
          height="34px"
          disabled={
            selectedParameters.length === 0 &&
            selectedConfidenceScore.length === 0
          }
        />
      )}
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        disablePortal={false}
        placement="bottom-start"
        style={{ marginTop: '0.25rem' }}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <AutocompleteWithCheckBoxes
            options={filters}
            handleChange={handleSelectedFiltersChange}
            defaultValue={selectedFilters}
            placeholder="Filter by"
          />
        </ClickAwayListener>
      </Popper>
    </Grid>
  );
};
export const LabelContainer = () => {
  const { classes } = makeStyles()({
    labelContainer: {
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      marginBottom: '1.5rem',
      borderRadius: '0.25rem',
      width: '25%',

      position: 'absolute',
      top: '20px',
      right: '24px',
    },
    labelColor: {
      height: '1rem',
      width: '1rem',
      borderRadius: '50%',
    },
    lableValueContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    tooltip: {
      backgroundColor: patientAccessTheme.palette.grey[900],
      padding: '10px',
      fontSize: '13px',
      fontFamily: 'Inter',
      fontWeight: 400,
      lineHeight: '16px',
      color: patientAccessTheme.palette.status.white,
    },
    arrow: {
      color: patientAccessTheme.palette.grey[900],
      marginLeft: '50px',
    },
    popper: {
      marginTop: '-8px',
      marginLeft: '-34px',
    },
  })();

  return (
    <Grid className={classes.labelContainer}>
      <Box
        sx={{
          padding: '1rem',
          borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
          bgcolor: patientAccessTheme.palette.grey[100],
        }}
      >
        <Typography
          fontSize="14px"
          fontType="semiBold"
          lineHeight="14px"
          textColor={patientAccessTheme.palette.text.mediumemphasis}
        >
          {messages.CONFIDENCE_SCORE}
        </Typography>
      </Box>
      <Box
        className={classes.lableValueContainer}
        sx={{
          padding: '0.5rem',
        }}
      >
        {messages.CONFIDENCE_SCORE_LIST.map((item) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            <div
              className={classes.labelColor}
              style={{ backgroundColor: item.color }}
            ></div>
            <Typography
              fontSize="14px"
              fontType="medium"
              lineHeight="28px"
              textColor={patientAccessTheme.palette.text.mediumemphasis}
            >
              {item?.name}
            </Typography>
            <Tooltip
              arrow
              classes={{
                tooltip: classes.tooltip,
                arrow: classes.arrow,
                popper: classes.popper,
              }}
              title={item?.tooltip}
              placement="bottom-start"
            >
              <InfoOutlinedIcon
                style={{
                  width: '18px',
                  height: '18px',
                  fill: patientAccessTheme.palette.status.icon,
                }}
              />
            </Tooltip>
          </Box>
        ))}
      </Box>
    </Grid>
  );
};
const InformationExtracted: React.FC<{}> = () => {
  const { classes } = makeStyles()({
    main: {
      width: '1276px',
      padding: '32px 20px 0px 20px',
    },
    parameterBox: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    tooltip: {
      backgroundColor: patientAccessTheme.palette.grey[900],
      padding: '10px',
      fontSize: '13px',
      fontFamily: 'Inter',
      fontWeight: 400,
      lineHeight: '16px',
      color: patientAccessTheme.palette.status.white,
    },
    arrow: {
      color: patientAccessTheme.palette.grey[900],
      marginLeft: '50px',
    },
    popper: {
      marginTop: '-8px',
      marginLeft: '-34px',
    },
    ellipsis: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      color: patientAccessTheme.palette.text.mediumemphasis,
      '&:hover': {
        overflow: 'visible',
        whiteSpace: 'normal',
        height: 'auto',
      },
    },
    underline: {
      '&:hover': {
        color: patientAccessTheme.palette.primary.main,
        textDecoration: 'underline dashed',
        textUnderlineOffset: '0.25rem',
        cursor: 'pointer',
      },
    },
  })();

  const {
    tableLoading,
    dueDiligenceForGroupData,
    setIsDetailsModalOpen,
    setCurrentModalData,
  } = useDueDiligenceContext();

  const handleModalOpen = (data: MADueDiligenceModalProps) => {
    setCurrentModalData(data);
    setIsDetailsModalOpen(true);
  };

  return (
    <Grid className={classes.main}>
      <CardComponent
        title={messages.TABLE_TITLE}
        body={
          <>
            <Grid
              container
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <FilterComponent />
              <LabelContainer />
            </Grid>

            {tableLoading ? (
              <CircularLoader />
            ) : (
              <CategoryTable
                columns={MADueDiligenceInternalTableColumns}
                rowsData={formatCategoryTableData(
                  dueDiligenceForGroupData,
                  classes,
                  handleModalOpen,
                  true,
                )}
                isCategory
              />
            )}
          </>
        }
      />
    </Grid>
  );
};
const Documents: React.FC<{}> = () => {
  const { classes } = makeStyles()({
    main: {
      width: '1276px',
      padding: '32px 20px 0px 20px',
    },
  })();

  const { dueDiligenceData } = useDueDiligenceContext();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const paginatedDocs = dueDiligenceData.documents_uploaded.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const totalPages = Math.ceil(
    dueDiligenceData.documents_uploaded.length / pageSize,
  );

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  return (
    <Grid className={classes.main}>
      <CardComponent
        title={messages.DOCUMENT_TABLE_TITLE}
        titleSubTitleMargin="1rem"
        body={
          <BasicTable
            columns={documentsTableColumns}
            rowsData={formatDocumentTableData(paginatedDocs)}
            displayType="box"
            width="100%"
            isFullTable
            headerCellHeight="48px"
            showPagination={true}
            pageCount={totalPages}
            handlePagination={handlePageChange}
            pageNumber={currentPage}
          />
        }
      />
    </Grid>
  );
};

const PageContents: React.FC<{}> = () => {
  const { classes } = makeStyles()({
    breadcrumbs: {
      padding: '12px 20px',
      borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      width: '100%',
    },
    main: {
      width: '1276px',
      padding: '32px 20px 0px 20px',
    },
    hr: {
      width: '100%',
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    },
    infoContainer: {
      marginTop: '1rem',
      display: 'flex',
      gap: '2rem',
      alignItems: 'center',
    },
    info: {
      display: 'flex',
      flexDirection: 'column',
    },
  })();

  const navigate = useNavigate();
  const {
    loading,
    dueDiligenceData,
    dueDiligenceForGroupData,
    isDetailsModalOpen,
    setIsDetailsModalOpen,
    currentModalData,
  } = useDueDiligenceContext();

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);

  const breadcrumbsItemsList = [
    [
      'text',
      'M&A Due Diligence',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => {
        navigate(MA_DUE_DILIGENCE);
      },
    ],
    [
      'text',
      `${dueDiligenceData?.company_name}`,
      {
        color: patientAccessTheme.palette.text.highemphasis,
        fontWeight: 600,
      },
    ],
  ];

  const handleTabIndexChange = (index: number) => {
    setSelectedTabIndex(index);
  };
  const handleExportCSV = () => {
    let csv =
      'Category,Parameter,Description,Answer,Confidence Score,Rationale\n';
    dueDiligenceForGroupData.map((item) => {
      item.data.map((row: any) => {
        csv += `"${item?.category}","${row.parameter}","${row.description}","${row.answer}","${row.confidence_score}","${row.score_rationale}"\n`;
      });
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${dueDiligenceData?.company_name}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleModalClose = () => {
    setIsDetailsModalOpen(false);
  };

  if (loading) {
    return <Loader />;
  }
  return (
    <Grid container>
      <Grid className={classes.breadcrumbs}>
        <Breadcrumbs itemList={breadcrumbsItemsList} />
      </Grid>
      <Grid className={classes.main}>
        <PageTitleAndSubTitle
          title={
            dueDiligenceData?.company_name +
            ' | ' +
            dueDiligenceData?.type_of_plan
          }
          subTitle={''}
          customButton
          customButtonText="Download Table"
          customButtonVariant="contained"
          customButtonClick={handleExportCSV}
          customButtonWidth="auto"
        />
      </Grid>
      <Box
        sx={{
          marginLeft: '20px',
          width: '100%',
        }}
      >
        <TabSelection
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={handleTabIndexChange}
          tabList={messages.TABS}
        />
      </Box>
      {selectedTabIndex === 0 && <InformationExtracted />}
      {selectedTabIndex === 1 && <Documents />}
      <BasicModal
        open={isDetailsModalOpen}
        handleClose={handleModalClose}
        title={messages.MODAL_TITLE}
        isSaveButton={false}
        isCloseBtn={false}
        width="920px"
        height="530px"
        isScrollable
      >
        <MADueDiligenceModal {...currentModalData} />
      </BasicModal>
    </Grid>
  );
};

const MADueDiligenceInternalPage: React.FC<{}> = () => {
  return (
    <DueDiligenceProvider>
      <PageContents />
    </DueDiligenceProvider>
  );
};

export default MADueDiligenceInternalPage;
